/**
 * Compliance Expiry Sweep + Review Queue — IN-1.d
 * ───────────────────────────────────────────────
 * Pure derivation. NEVER mutates. NEVER auto-suspends. Produces
 * advisory suggestions an admin can review in the Compliance Review
 * Queue (Soft Enforcement default).
 *
 * Joins:
 *   • Contractor universe
 *   • Document ledger (IN-1.b)
 *   • getComplianceStatus(contractor, documents, parent?) — IN-1.b
 *
 * Anchor: RECONCILIATION.md §15 (IN-1.d)
 */

import type { Contractor } from "@/data/types";
import type { ComplianceDocument } from "./complianceDocuments";
import {
  getComplianceStatus,
  type ComplianceRequirementKey,
  type ComplianceStatus,
  type OverallComplianceStatus,
  type RequirementResolution,
} from "./complianceStatus";

export type ComplianceSuggestionKind =
  | "doc_expired"
  | "doc_expiring_soon"
  | "doc_missing"
  | "doc_pending_verification"
  | "doc_rejected";

export interface ComplianceSuggestion {
  contractorId: string;
  requirement: ComplianceRequirementKey;
  kind: ComplianceSuggestionKind;
  documentId?: string;
  expiresAt?: string;
}

const STATUS_TO_KIND: Partial<
  Record<RequirementResolution["status"], ComplianceSuggestionKind>
> = {
  expired: "doc_expired",
  expiring_soon: "doc_expiring_soon",
  missing: "doc_missing",
  pending_verification: "doc_pending_verification",
  rejected: "doc_rejected",
};

/**
 * Sweep every contractor's compliance status into a flat suggestion
 * list. Pure. No side effects.
 */
export function getComplianceExpirySuggestions(
  contractors: Contractor[],
  documents: ComplianceDocument[],
  now: Date = new Date(),
): ComplianceSuggestion[] {
  const byId = new Map(contractors.map((c) => [c.id, c]));
  const out: ComplianceSuggestion[] = [];
  for (const c of contractors) {
    // Skip clearly blocked contractors — they're already in the
    // suspended bucket; their compliance gaps aren't actionable.
    if (c.vettingStatus === "blocked") continue;
    const parent = c.parentEntityId ? byId.get(c.parentEntityId) : undefined;
    const status = getComplianceStatus(c, documents, parent, now);
    for (const r of status.requirements) {
      if (!r.required) continue;
      const kind = STATUS_TO_KIND[r.status];
      if (!kind) continue;
      out.push({
        contractorId: c.id,
        requirement: r.requirement,
        kind,
        documentId: r.documentId,
        expiresAt: r.expiresAt,
      });
    }
  }
  return out;
}

export type ReviewQueueBucket =
  | "missing"
  | "pending_verification"
  | "expired"
  | "expiring_soon"
  | "ready"
  | "suspended";

export interface ReviewQueueRow {
  contractor: Contractor;
  bucket: ReviewQueueBucket;
  status: ComplianceStatus | null;
}

/**
 * Bucketed review queue. Each contractor lands in exactly one bucket
 * by deterministic priority: suspended > missing > expired >
 * pending_verification > expiring_soon > ready (ok / not_required).
 *
 * Pure. Used by the admin Compliance Review page (IN-1.d UI).
 */
export function getComplianceReviewQueue(
  contractors: Contractor[],
  documents: ComplianceDocument[],
  now: Date = new Date(),
): ReviewQueueRow[] {
  const byId = new Map(contractors.map((c) => [c.id, c]));
  return contractors.map((c) => {
    if (c.vettingStatus === "blocked") {
      return { contractor: c, bucket: "suspended" as const, status: null };
    }
    const parent = c.parentEntityId ? byId.get(c.parentEntityId) : undefined;
    const status = getComplianceStatus(c, documents, parent, now);
    return {
      contractor: c,
      bucket: mapOverallToBucket(status.overall),
      status,
    };
  });
}

function mapOverallToBucket(overall: OverallComplianceStatus): ReviewQueueBucket {
  switch (overall) {
    case "missing":
      return "missing";
    case "expired":
      return "expired";
    case "pending_verification":
      return "pending_verification";
    case "expiring_soon":
      return "expiring_soon";
    case "ok":
    case "not_required":
    default:
      return "ready";
  }
}

export const REVIEW_QUEUE_BUCKET_ORDER: ReviewQueueBucket[] = [
  "missing",
  "expired",
  "pending_verification",
  "expiring_soon",
  "ready",
  "suspended",
];
